// The Kratos browser login, with cookies: the flow the Flutter page used
// (auth_repository_ory.dart) — a login flow with refresh=true, then the
// password method with the flow's CSRF token. Kratos sets the session cookie
// on success. Failures are thrown with Kratos' own text, so the form shows it.

import type { Config } from './config';

export class LoginError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'LoginError';
  }
}

interface UiText {
  text?: string;
}
interface Flow {
  id?: string;
  ui?: {
    messages?: UiText[];
    nodes?: { attributes?: { name?: string; value?: unknown }; messages?: UiText[] }[];
  };
  error?: { reason?: string; message?: string };
}

async function kratos(fetchFn: typeof fetch, url: string, init: RequestInit = {}): Promise<{ status: number; body: Flow & Record<string, unknown> }> {
  const res = await fetchFn(url, {
    credentials: 'include',
    ...init,
    headers: { Accept: 'application/json', ...(init.headers ?? {}) },
  });
  const text = await res.text();
  return { status: res.status, body: text ? JSON.parse(text) : {} };
}

/** The first message Kratos gives for a failed flow, or a fallback. */
export function flowMessage(f: Flow, fallback: string): string {
  const all = [...(f.ui?.messages ?? []), ...(f.ui?.nodes ?? []).flatMap((n) => n.messages ?? [])];
  return all.find((m) => m.text)?.text ?? f.error?.reason ?? f.error?.message ?? fallback;
}

export async function login(cfg: Config, identifier: string, password: string, fetchFn: typeof fetch = fetch): Promise<void> {
  const init = await kratos(fetchFn, `${cfg.kratosPublic}/self-service/login/browser?refresh=true`);
  if (init.status !== 200 || !init.body.id) {
    throw new LoginError(flowMessage(init.body, `Could not start a login (HTTP ${init.status})`), init.status);
  }
  const csrf = init.body.ui?.nodes?.find((n) => n.attributes?.name === 'csrf_token')?.attributes?.value;
  const res = await kratos(fetchFn, `${cfg.kratosPublic}/self-service/login?flow=${encodeURIComponent(init.body.id)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ method: 'password', identifier, password, csrf_token: String(csrf ?? '') }),
  });
  if (res.status !== 200) {
    throw new LoginError(flowMessage(res.body, `Login failed (HTTP ${res.status})`), res.status);
  }
}

export async function logout(cfg: Config, fetchFn: typeof fetch = fetch): Promise<void> {
  const flow = await kratos(fetchFn, `${cfg.kratosPublic}/self-service/logout/browser`);
  const token = flow.body['logout_token'];
  if (flow.status === 401) return; // no session: already logged out
  if (flow.status !== 200 || typeof token !== 'string') {
    throw new LoginError(flowMessage(flow.body, `Logout failed (HTTP ${flow.status})`), flow.status);
  }
  const res = await kratos(fetchFn, `${cfg.kratosPublic}/self-service/logout?token=${encodeURIComponent(token)}`);
  if (res.status !== 204 && res.status !== 200) {
    throw new LoginError(`Logout failed (HTTP ${res.status})`, res.status);
  }
}
